import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {
    IDictionaryEntry,
    IExportGlossary,
    IGlossaryEntry,
    IMetaData,
    ISelection,
    ISentence,
    IText,
    IWord
} from "../../api/data-model";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {PIPELINE_STATUS, PipelineEvent} from "../../event/PipelineEvent";
import {EventBus} from "../../event/EventBus";
import {CssSelection, CssSentence, CssText, CssWord} from "../../content/content-view.objects";
import {GlossaryPipelineService} from "./glossary.pipeline.service";
import {QuestionPipelineService} from "./question.pipeline.service";
import {DictionaryPipelineService} from "./dictionary.pipeline.service";
import {WordAudioPipelineService} from "./word-audio.pipeline.service";
import {ScriptPipelineService} from "./script.pipeline.service";
import {BatchPipelineService, ProcessingTask} from "./batch.pipeline.service";
import {VerificationAudioCategory, VerifierPipelineService} from "./verifier.pipeline.service";
import {VocabularyPipelineService} from "./vocabulary.pipeline.service";
import {ClientConfiguration} from "../configuration/client.config";
import {UtilitiesService} from "../utilities.service";
import {AudioQueueService} from "../audio-playback/audioqueue.service";
import {Scilearn} from "../../constants";
import {HttpClient} from "@angular/common/http";
import {catchError, filter, finalize, flatMap, map} from "rxjs/operators";
import {forkJoin} from "rxjs/internal/observable/forkJoin";
import {of} from "rxjs/internal/observable/of";
import {Router} from "@angular/router";
import {PipelineErrorEvent} from "../../pipeline/PipelineErrorEvent";
import {ConversionPipelineService} from "./conversion.pipeline.service";
import {WordCategoryPipelineService} from "./word-category.pipeline.service";

export interface IGlossaryInfo {
    entry: IGlossaryEntry;
    isDefinition: boolean;
}

@Injectable()
export class PipelineService {

    public static CONTENT_S3:string = "https://s3-us-west-1.amazonaws.com/racontent/staging/";
    // private CONTENT_S3_SELECTIONS:string = PipelineService.CONTENT_S3 + "selections/";
    public static CONTENT_SERVER: string = "http://ec2-18-144-34-2.us-west-1.compute.amazonaws.com:7777/";
    // public static CONTENT_SERVER = "http://localhost:7777/";
    // public static CONTENT_SERVER = "http://10.33.0.63:7777/";
    public static CONTENT_SELECTIONS: string = PipelineService.CONTENT_SERVER + "selection?selectionName=";
    public static CONTENT_METADATA: string = PipelineService.CONTENT_SERVER + "generateCompleteMetadata";

    public currentSourceSelection: ISelection;
    public currentSelection: ISelection;
    public titles: Array<IMetaData> = new Array<IMetaData>();

    private expansionsAddedSelections: ISelection[];
    private glossaryService: GlossaryPipelineService;
    private questionService: QuestionPipelineService;
    private dictionaryService: DictionaryPipelineService;
    private wordAudioService: WordAudioPipelineService;
    private scriptPipelineService: ScriptPipelineService;
    private batchPipelineService: BatchPipelineService;
    private verifierPipelineService: VerifierPipelineService;
    public vocabularyPipelineService: VocabularyPipelineService;
    public conversionPipelineService: ConversionPipelineService;
    public wordCategoryPipelineService: WordCategoryPipelineService;
    private isInitialized = false;

    constructor(
        private logService: BugsnagLoggingService,
        private audioQueueService: AudioQueueService,
        private config: ClientConfiguration,
        private eventBus: EventBus,
        private http: HttpClient,
        private router: Router
    ) {
        let sub = this.eventBus.pipe(filter((e: PipelineEvent) =>
            (e.type === PipelineEvent.TYPE)))
            .subscribe((e: PipelineEvent) => {
                console.log("Initializing pipeline service...");
                if (!this.isInitialized) {
                    if (!!config.getContentToolServerURL()) {
                        PipelineService.CONTENT_SERVER = config.getContentToolServerURL();
                        PipelineService.CONTENT_SELECTIONS = PipelineService.CONTENT_SERVER + "selection?selectionName=";
                        PipelineService.CONTENT_METADATA = PipelineService.CONTENT_SERVER + "generateCompleteMetadata";
                    }
                    this.glossaryService = new GlossaryPipelineService(http, PipelineService.CONTENT_SERVER,
                        logService, eventBus);
                    this.questionService = new QuestionPipelineService(http, logService);
                    this.dictionaryService = new DictionaryPipelineService(http, PipelineService.CONTENT_S3);
                    this.wordAudioService = new WordAudioPipelineService(http, PipelineService.CONTENT_S3,
                        PipelineService.CONTENT_SERVER);
                    this.verifierPipelineService = new VerifierPipelineService(http, this.audioQueueService,
                        this.config, this.eventBus);
                    this.scriptPipelineService = new ScriptPipelineService(http, this.wordAudioService, this.glossaryService);
                    this.batchPipelineService = new BatchPipelineService(http, this.dictionaryService,
                        this.wordAudioService, this.glossaryService,
                        this.verifierPipelineService);
                    this.vocabularyPipelineService = new VocabularyPipelineService(this.glossaryService, logService);
                    this.conversionPipelineService = new ConversionPipelineService(logService, this.dictionaryService, http);
                    this.wordCategoryPipelineService = new WordCategoryPipelineService(logService, http);
                    this.isInitialized = true;
                }
            });
    }

    static isSuperKey(value: string): boolean {
        return ["[", "]", "}"].indexOf(value) > -1;
    }

    handleKeyboardEvent(event: KeyboardEvent): void {
        // Since the pipeline keys are "meta" keys (they launch popups that act on/select from a list of stories),
        // for workflow and data integrity purposes it seems better to only allow launch from the library.
        // Might not be the final answer as things evolve
        if (!this.router.url || !this.router.url.match("/library")) {
            return;
        }

        switch (event.key.toLowerCase()) {
            case "[":
                this.logService.log("[ pressed, open content pipeline");
                this.eventBus.next(new PipelineEvent(PIPELINE_STATUS.manual));
                break;
            case "]":
                this.logService.log("] pressed, open script generator");
                this.eventBus.next(new PipelineEvent(PIPELINE_STATUS.script));
                break;
            case "}":
                this.logService.log("} pressed, open batch change modal");
                this.eventBus.next(new PipelineEvent(PIPELINE_STATUS.batch));
                break;

            default:
        }
    }

    public isDevMode()  {
        return this.config.isSuperUserDevMode;
    }

    public retrieveUninflatedContent(name: string): Observable<ISelection> {
        let selectionURL = PipelineService.CONTENT_SELECTIONS +
            name + "-source.json" + UtilitiesService.cacheBuster("&");

        return this.http.get(selectionURL, {withCredentials: true})
            .pipe(map(data => {
                let response: any = data;
                let previousSelectionID = this.currentSourceSelection ?
                    this.currentSourceSelection.metaData.referenceStory : "";
                this.currentSourceSelection = response;
                if (response.metaData.referenceStory !== previousSelectionID) {
                    this.eventBus.next(new PipelineEvent(PIPELINE_STATUS.selectionUpdated));
                }
                return response;
            }), catchError(this.handleError("Get selection ", null)));

    }

    public retrieveContent(name: string): Observable<ISelection> {
        return this.http.get(PipelineService.CONTENT_SELECTIONS + name + ".json" +
                             UtilitiesService.cacheBuster("&"), { withCredentials: true })
            .pipe(map(data => {
                let response: any = data;
                this.currentSelection = response;
                return response;
            }));
    }

    // TODO: figure out how an error manifests here, and test triggering the error modal
    public saveSourceContent(): Observable<Response> {
        const name = this.currentSourceSelection?.metaData?.referenceStory;
        console.log("name on save " + name);
        return this.http.post(
            PipelineService.CONTENT_SERVER + "selectionSource",
            this.formatSelection(this.currentSourceSelection), { withCredentials: true }
        ).pipe(map(response => {
            console.log("success:" + response);
            return response;
        }), catchError(this.handleError("Save selection " +
            name, null))) as Observable<Response>;
    }

    public saveContent(): Observable<Response> {
        this.logService.log("saving inflated content");
        return this.http.post(
            PipelineService.CONTENT_SERVER + "selection",
            this.formatSelection(this.currentSelection), { withCredentials: true }
        ) as Observable<Response>;
    }

    public inflateContent(): Observable<Response> {
        return this.glossaryService.loadGlossary().pipe(flatMap(response => {
            this.logService.log("inflating content after save");
            this.currentSelection = (JSON.parse(JSON.stringify(this.currentSourceSelection)));
            this.currentSelection.pronunciationDictionary =
                this.dictionaryService.addDictionary(this.currentSelection);
            this.currentSelection.glossaryEntries = this.glossaryService.addGlossaryUsingV1Id(this.currentSelection);
            return this.saveContent();
        }));
    }

    public importGlossary(tsv): void {
        // import glossary file
        this.glossaryService.importGlossary(tsv);
    }

    public importQuestions(tsv): void {
        // import questions - first call returns map of question entries by referenceStory; load those selections,
        // replace questions, and save
            let selections = this.questionService.setupImportData(tsv);
            console.log("completed initial import ", selections);
            console.log(tsv);
            for (let selectionName of selections) {
                this.retrieveUninflatedContent(selectionName).subscribe(selection => {
                    this.questionService.addQuestions(selection);
                    this.currentSourceSelection = selection;
                    this.saveSourceContent().subscribe(result => {
                        console.log("saved content: " + result);
                    });
                });
            }
    }

    /*public async convertSelections(): Promise<void> {
        this.conversionPipelineService.convert().subscribe((json) => {
            console.log("completed convert test: ");
            console.log(json);
            json.forEach(selection => {
                if (!!selection && !!selection.metaData && !!selection.metaData.referenceStory) {
                    this.saveUpdatedContent(selection);
                }
            });
        });
    }*/

    public async convertSelections(json): Promise<void> {
        const selections = this.conversionPipelineService.convert(json);
        console.log("completed convert test: ");
        console.log(json);
        selections.forEach(selection => {
            this.saveUpdatedContent(selection);
        });
    }

    public getGlossaryTerm(word: CssWord, sentence: CssSentence): string {
        return this.glossaryService.getGlossaryTerm(word, sentence);
    }

    public searchGlossary(name: string): Observable<IGlossaryEntry[]> {
        return this.glossaryService.searchGlossary(name);
    }

    public getStoredGlossary(name: string): IGlossaryEntry[] {
        return this.glossaryService.getEntry(name);
    }

    public getStoredGlossaryFromID(id: string): IGlossaryEntry {
        return this.glossaryService.getEntryUsingV1Id(id).entry;
    }

    public saveGlossary(glossaryEntry: IGlossaryEntry): Observable<string> {
        this.replaceHyphensWithEmDashInText(glossaryEntry);
        return this.glossaryService.saveGlossary(glossaryEntry);
    }

    public deleteGlossary(glossaryEntry: IGlossaryEntry): Observable<string> {
        return this.glossaryService.deleteGlossary(glossaryEntry);
    }

    public tagGlossary(selectedWord:CssWord, glossaryInfo:IGlossaryInfo, linkTerm: string, texts:IText[]): IText[] {
        let initialText = this.formatSelection(texts);
        let glossaryEntry = glossaryInfo.entry;
        let glossarySequence = (linkTerm !== "") ? linkTerm.toLowerCase().split(/\s+/) :
                               glossaryEntry.word.toLowerCase().split(/\s+/);
        // for single words, allow linking to other forms e.g. adapted, adapting --> adapt
        if (glossarySequence.length === 1) {
            let word = selectedWord ? selectedWord.word.toLowerCase() : glossarySequence[0];
            if (word !== glossarySequence[0]) {
                let action = "linking";
                if (!glossaryInfo.isDefinition) {
                    action = "unlinking";
                }
                this.logService.log(action + " instances of selected word: " + word +
                    " to non-matching entry word: " + glossarySequence[0]);
                glossarySequence[0] = word;
            }
        }
        if (glossaryEntry.translations.length > 1) {
            alert("multiple translations for glossary sequence (using first in list): "
                + glossaryEntry.translations[0].word);
        }
        texts.forEach((text:IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    for (let i = 0; i < sentence.words.length - glossarySequence.length + 1; i++) {
                        let sentenceSequence:IWord[] = sentence.words.slice(i, i + glossarySequence.length);
                        if (this.foundGlossaryMatch(glossarySequence, sentenceSequence)) {
                            if (glossaryInfo.isDefinition) {
                                if (!this.inLongerPhraseAlready(sentence, i, glossarySequence)) {
                                    this.linkGlossary(sentenceSequence, glossaryEntry);
                                }
                            } else {
                                this.unlinkGlossary(sentenceSequence);
                            }
                        }
                    }

                });
            }
        });
        if (this.formatSelection(texts) === initialText) {
            return null;

        }
        return texts;

    }

    private inLongerPhraseAlready(sentence: ISentence, index: number, newGlossarySequence: string[]): boolean {
        let inLongerPhrase = false;
        let glossaryId = sentence.words[index].glossary;
        if (glossaryId) {
            let i = index;
            let startIndex = index;
            let endIndex = index;
            while (i >= 0 && sentence.words[i].glossary && sentence.words[i].glossary === glossaryId) {
                startIndex = i;
                i--;
            }
            while (endIndex < sentence.words.length && sentence.words[endIndex].glossary &&
                   sentence.words[endIndex].glossary === glossaryId) {
                endIndex++;
            }
            inLongerPhrase = (endIndex - startIndex) > newGlossarySequence.length;
            if (inLongerPhrase) {
                let existingPhrase = sentence.words.slice(startIndex, endIndex).map(w => w.word).join(" ");
                alert("Not overriding existing linkage for term " + newGlossarySequence.join(" ") +
                      " where linked in phrase: " + existingPhrase);
            }
        }
        return inLongerPhrase;
    }

    private linkGlossary(sentenceSequence: IWord[], glossaryEntry: IGlossaryEntry): void {
        for (let j = 0; j < sentenceSequence.length; j++) {
            sentenceSequence[j].glossary = glossaryEntry.id;
            // TARGET category words are words that are treated more strictly in recognition because
            // they are important or new vocabulary.  Anything that has glossary linked to it should
            // be tagged as a target category word
            if (sentenceSequence[j].category === Scilearn.WORD_CATEGORY_DEFAULT) {
                sentenceSequence[j].category = Scilearn.WORD_CATEGORY_TARGET;
            }
            let translationId = glossaryEntry.translations.length > 0 ?
                glossaryEntry.translations[0].id : "NO TRANSLATION";
            sentenceSequence[j].translations = {"es": translationId};
            console.log("linked word: " + JSON.stringify(sentenceSequence[j]));

        }
    }

    private unlinkGlossary(sentenceSequence: IWord[]): void {
        for (let j = 0; j < sentenceSequence.length; j++) {
            delete sentenceSequence[j].glossary;
            delete sentenceSequence[j].translations;
            // Remove the TARGET categorization if unlinking glossary from a word or phrase
            if (sentenceSequence[j].category === Scilearn.WORD_CATEGORY_TARGET) {
                sentenceSequence[j].category = Scilearn.WORD_CATEGORY_DEFAULT;
            }
            console.log("modified word: " + JSON.stringify(sentenceSequence[j]));

        }
    }

    private foundGlossaryMatch(glossarySequence:string[], sentenceSequence:IWord[]):boolean {
        let isMatch = true;
        for (let i = 0; i < glossarySequence.length; i++) {
            isMatch = isMatch &&
                (UtilitiesService.convertPunctuationToASCII(glossarySequence[i]) ===
                UtilitiesService.convertPunctuationToASCII(sentenceSequence[i].word.toLowerCase()));

        }
        return isMatch;
    }

    public analyzeGlossary(): string {
        let cssSelection: CssSelection = new CssSelection(this.currentSourceSelection);
        let texts = cssSelection.text;
        let glossary = cssSelection.glossaryEntries;
        this.verifierPipelineService.resetGlossaryErrors();
        texts.forEach((text: CssText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: CssSentence) => {
                    sentence.words.forEach((word: CssWord) => {
                        if (word.glossary) {
                            // check if glossary entry exists (must get whole phrase for error logging)
                            let glossaryTerm = this.getGlossaryTerm(word, sentence).toLowerCase();
                            this.verifierPipelineService.analyzeErrorsForWord(glossaryTerm, glossary, word);
                        }
                    });
                });
            }
        });

        // iterate over glossaryErrors object, concat strings to glossaryErrorString
        return this.verifierPipelineService.glossaryErrorString();
    }

    public getMissingAudioData():any {
        return this.verifierPipelineService.missingAudioData;
    }

    public resetMissingAudioDataOfSelections():void {
        this.verifierPipelineService.resetMissingAudioDataOfSelections();
    }

    public analyzeIntoAndTitleAudio(): Observable<string> {
        return this.verifierPipelineService.playIntoAndTitleAudio(this.currentSourceSelection, false).pipe(map((errorString) => {
            return errorString;
        }));
    }

    public analyzeReadToMeAudio(): Observable<string> {
        return this.verifierPipelineService.playReadToMeAudio(this.currentSourceSelection, false).pipe(map((errorString) => {
            return errorString;
        }));
    }

    public analyzeQuestionAudio(): Observable<string> {
        return this.verifierPipelineService.playQuestionAudio(this.currentSourceSelection, false).pipe(map((errorString) => {
            return errorString;
        }));
    }

    public analyzeWordAudio(): Observable<string> {
        return this.verifierPipelineService.playWordAudio(this.currentSourceSelection, this.wordAudioService, false)
            .pipe(map((errorString) => {
            return errorString;
        }));
    }

    public analyzeGlossaryAudio(): Observable<string> {
        let glossaryEntries: IExportGlossary = this.glossaryService.addGlossaryUsingV1Id(this.currentSourceSelection);
        return this.verifierPipelineService.playGlossaryAudio(
            this.currentSourceSelection, glossaryEntries, false).pipe(map((errorString) => {
            return errorString;
        }));
    }

    public async analyzeSelectionImages(): Promise<any[]> {
        return await this.verifierPipelineService.checkSelectionImageData(this.currentSourceSelection);
    }

    public transitionAudioState(): void {
        this.verifierPipelineService.transitionAudioState();
        console.log(this.currentSourceSelection);
        switch (this.verifierPipelineService.audioCategory) {
            case VerificationAudioCategory.SELECTION:
                this.saveSourceContent().subscribe(result => {
                    this.logService.log("saved selection after QA verify");
                });
                break;
            case VerificationAudioCategory.GLOSSARY:
                this.glossaryService.postGlossary().subscribe(result => {
                    this.logService.log("saved glossary after QA verify");
                });
                break;
            case VerificationAudioCategory.WORD_AUDIO:
                this.wordAudioService.postWordAudioTable().subscribe(result => {
                    this.logService.log("saved word audio table after QA verify");
                });
                break;
            default:
                this.logService.log("unknown audio category");
        }
    }


    public analyzeSelectionMetaData(): boolean {
        return this.verifierPipelineService.checkSelectionMetaData(this.currentSourceSelection);
    }

    public exportDictionary(): void {
        this.dictionaryService.saveDictionary(this.batchPipelineService.pronunciationSelections, true);
    }

    public saveDictionary(): void {
        this.dictionaryService.saveDictionary(this.batchPipelineService.pronunciationSelections, false);
    }

    public saveDictionaryEntry(entry: IDictionaryEntry): void {
        if (entry.word) {
            this.dictionaryService.updateWord(entry);
        }
    }

    public exportExpansions(): void {
        this.dictionaryService.saveExpansions(this.batchPipelineService.expansionsList, true);
    }

    public saveExpansions(): void {
        this.dictionaryService.saveExpansions(this.batchPipelineService.expansionsList, false);
    }

    public saveExpansionEntry(word: string, expansions: string[]): void {
        this.batchPipelineService.addExpansion(word, expansions);
        const words: Set<string> = expansions.reduce((accumulator, phrase) => {
            phrase.split(" ").forEach(w => accumulator.add(w));
            return accumulator;
        }, new Set() as Set<string>);
        words.forEach(expansionWord => {
            let dictionaryEntry: IDictionaryEntry = this.dictionaryService.getWord(expansionWord);
            if (!dictionaryEntry.pronunciations || dictionaryEntry.pronunciations.length === 0) {
                this.batchPipelineService.addMissingWord(expansionWord.toUpperCase());
            }
        });


    }

    public getNearMatches(word: string, numMatches: number): IDictionaryEntry[] {
        return this.dictionaryService.getNearMatches(word, numMatches);
    }

    public getMissingWord(index: number): string {
        return this.batchPipelineService.getMissingWord(index);
    }

    public removeEntry(word: string): void {
        return this.batchPipelineService.removeEntry(word);
    }

    public numMissingEntries(): number {
        return this.batchPipelineService.numMissingEntries();
    }

    public pronunciationsValid(entry: IDictionaryEntry): boolean {
        return this.dictionaryService.checkDictionaryEntry(entry);
    }

    private formatSelection(selection):string {
        return JSON.stringify(selection, null, 4);
    }

    public replaceHyphensWithEmDashInText(selectionDataObject: Object): void {
        // works for question and glossary objects; if want to apply to selection text, will need to revisit
        Object.keys(selectionDataObject).forEach(key => {
            if (key === "text" && (typeof selectionDataObject[key] === "string")) {
                selectionDataObject[key] = UtilitiesService.replaceHyphensWithEmDash(selectionDataObject[key]);
            }
            if ((selectionDataObject[key] !== null) && (typeof selectionDataObject[key] === "object")) {
                this.replaceHyphensWithEmDashInText(selectionDataObject[key]);
            }
        });
    }

    public fetchMetadata():Observable<Response> {
        return this.http.get<Response>(PipelineService.CONTENT_SERVER + "metadatas", { withCredentials: true });
    }

    public populateData(filterNew: boolean, filterPublished): Observable<IMetaData[]> {
        return this.glossaryService.loadGlossary().pipe(flatMap(response => {
            return this.fetchMetadata().pipe(map((results:any) => {
                for (let metadata of results) {
                    if (metadata) {
                        let titleFound = this.titles.find((title: IMetaData) => {
                            return (title.referenceStory === metadata.referenceStory);
                        });
                        if (!titleFound) {
                            this.titles.push(metadata);
                        }
                    }
                }
                return this.filterTitles("", filterNew, filterPublished);
            }));
        })) as Observable<IMetaData[]>;
    }

    public filterTitles(search: string, filterNew: boolean, filterUnpublished: boolean):IMetaData[] {
        let filteredTitles:IMetaData[] = [];
        // this.filteredTitles = new Array<IMetaData>();
        for (let title of this.titles) {
            if (!search || search === ""
                || title.referenceStory.toLowerCase().indexOf(search.toLowerCase()) >= 0
                || title.shortTitle.toLowerCase().indexOf(search.toLowerCase()) >= 0
            ) {
                if (!filteredTitles.includes(title)) {
                    let doInclude = true;
                    if (filterNew) {
                        if (![ConversionPipelineService.CONTENT_GROUP, ...ConversionPipelineService.UPCOMING_RELEASES]
                            .includes(title.contentVersion)) {
                            doInclude = false;
                        }
                    }
                    if (filterUnpublished) {
                        if (title.contentVersion !== "UNPUBLISHED") {
                            doInclude = false;
                        }
                    }
                    if (doInclude) {
                        filteredTitles.push(title);
                    }
                }
            }
        }
        return filteredTitles;
    }

    public exportScripts(selectionList: string[], isTestRun: boolean): void {
        this.scriptPipelineService.initScripts();
        let observableArray = [];
        selectionList.forEach((selectionName) => {
            this.logService.log("adding selection to array: " + selectionName);
            observableArray.push(this.retrieveUninflatedContent(selectionName));
        });
        this.fetchSharedData().subscribe((result) => {
            this.scriptPipelineService.exportSharedData("initial");
            forkJoin(observableArray).subscribe((selections: ISelection[]) => {
                this.scriptSelections(selections);
                this.scriptPipelineService.exportSharedData("final");
                this.saveUpdatedData(selections, isTestRun);
                this.scriptPipelineService.exportGlossaryScript();
                this.scriptPipelineService.exportWordAudioScript();
            });
        });
    }

    public batchProcess(selectionList: string[], isTestRun: boolean, task: ProcessingTask): void {
        this.logService.log("calling batch process with isTestRun:" + isTestRun);
        this.batchPipelineService.clearErrors();

        let observableArray = [];
        selectionList.forEach((selectionName) => {
            this.logService.log("adding selection to array: " + selectionName);
            observableArray.push(this.retrieveUninflatedContent(selectionName));
        });

        forkJoin(observableArray).subscribe((selections: ISelection[]) => {
            // this.analyzeSelections(selections);
            this.processSelections(selections, task);
            selections.forEach(selection => {
                if (selection === null) {
                    this.logService.log("found unprocessed selection just before save");
                }
            });
            this.logService.log("num selections: " + selections.length);
            let filteredSelections = selections.filter((selection) => selection !== null);
            this.logService.log("num filtered selections: " + filteredSelections.length);
            this.saveUpdatedSelections(selections.filter((selection) => selection !== null), isTestRun);
            this.logService.log("downloading error file ");
            this.batchPipelineService.downloadErrors();
        });
    }

    public batchUpdateRecognizerData(selectionList: string[]): void {
        // TODO: store selectionList (or maybe selections) for later updating
        this.logService.log("calling batch update prons");
        this.batchPipelineService.clearErrors();

        let observableArray = [];
        selectionList.forEach((selectionName) => {
            this.logService.log("adding selection to array: " + selectionName);
            observableArray.push(this.retrieveUninflatedContent(selectionName));
        });

        forkJoin(observableArray).subscribe((selections: ISelection[]) => {
            this.addExpansions(selections);
            this.analyzePronunciations(selections);
            this.expansionsAddedSelections = selections;
            // launch modal for editing.
            this.eventBus.next(new PipelineEvent(PIPELINE_STATUS.pronunciations));
        });
    }

    public saveExpandedSelections(): void {
        // need to call this here again, because new expansions may have been added
        this.addExpansions(this.expansionsAddedSelections);
        this.expansionsAddedSelections.forEach(selection => {
            this.currentSourceSelection = selection;
            this.saveSourceContent().subscribe(result => {
                this.logService.log("saved uninflated, done result.ok=" + result?.ok);
            });
        });
    }

    public batchRenumberQuestions(fullSelectionList: string[], selectionList: string[], isTestRun: boolean): void {
        this.batchPipelineService.clearQuestionData();
        let observableArray = [];
        fullSelectionList.forEach((selectionName) => {
            this.logService.log("adding selection to array: " + selectionName);
            observableArray.push(this.retrieveUninflatedContent(selectionName));
        });

        forkJoin(observableArray).subscribe((selections: ISelection[]) => {
            this.analyzeSelections(selections);
            this.batchPipelineService.initQuestionData();
            this.batchProcess(selectionList, isTestRun, ProcessingTask.QUESTION_IDS);
        });
    }

    public batchVerify(selectionList: string[]): void {
        this.logService.log("calling batch audio verify");
        this.batchPipelineService.clearErrors();
        let observableArray = [];
        let selectionIndex = 0;
        if (selectionList.length) {
            selectionList.forEach((selectionName) => {
                observableArray.push(this.retrieveUninflatedContent(selectionName));
            });

            forkJoin(observableArray).subscribe((selections: ISelection[]) => {
                this.batchPipelineService.batchVerify(selections[selectionIndex]).subscribe((result) => {
                    if (result === "DONE") {
                        this.doNextVerify(selections, selectionIndex++);
                    }
                });
            });
        }
    }

    private doNextVerify(selections: ISelection[], index): void {
        if (index < selections.length) {
            this.batchPipelineService.batchVerify(selections[index]).subscribe((result) => {
                if (result === "DONE") {
                    this.doNextVerify(selections, index++);
                }
            });
        } else {
            console.log("downloading errors");
            this.batchPipelineService.downloadErrors();
            this.saveUpdatedData(selections, false);

        }
    }

    public batchInflate(selectionList: string[]): void {
        this.logService.log("calling batch inflate");
        let observableArray = [];
        selectionList.forEach((selectionName) => {
            this.logService.log("adding selection to array: " + selectionName);
            observableArray.push(this.retrieveUninflatedContent(selectionName));
        });
        forkJoin(observableArray).subscribe((selections: ISelection[]) => {
            this.saveUpdatedSelections(selections, false);
        });
    }

    private scriptSelections(selections: ISelection[]): void {
        selections.forEach(selection => {
            let exportContent = this.scriptPipelineService.exportScript(selection);
            let exportGlossary = this.glossaryService.addGlossaryUsingV1Id(selection);
            this.scriptPipelineService.collectGlossary(exportGlossary);
        });
    }

    private processSelections(selections: ISelection[], task: ProcessingTask): void {
        selections.forEach((selection, index) => {
            selections[index] = this.batchPipelineService.batchProcess(selection, task);
            if (selections[index] === null) {
                console.log("found unprocessed selection");
            }
        });
    }

    private analyzeSelections(selections: ISelection[]): void {
        selections.forEach((selection, index) => {
            // TODO: may want switch here to do other kinds of analysis
            this.batchPipelineService.batchAnalyzeQuestionNumbers(selection);
        });
    }

    private analyzePronunciations(selections: ISelection[]): void {
        selections.forEach((selection, index) => {
            this.batchPipelineService.batchAnalyzePronunciations(selection);
        });
        this.batchPipelineService.alphabetizeMissingProns();
    }

    private addExpansions(selections: ISelection[]): void {
        selections.forEach((selection, index) => {
            this.batchPipelineService.batchAddExpansions(selection);
        });
    }

    private saveUpdatedData(selections: ISelection[], isTestRun: boolean): void {
        this.postSharedData(isTestRun).subscribe((result) => {
            this.saveUpdatedSelections(selections, isTestRun);
        });

    }

    private saveUpdatedSelections(selections: ISelection[], isTestRun: boolean): void {
        selections.forEach((selection) => {
            this.logService.log("updating content for: " + selection.metaData.referenceStory);
            if (!selection.metaData.isHighInterestLowLevel) {
                selection.metaData.isHighInterestLowLevel = false;
            }
            if (isTestRun) {
                // download both uninflated and inflated.
                UtilitiesService.downloadFile(this.formatSelection(selection),
                                              selection.metaData.referenceStory + "-source.json");
                let exportSelection = (JSON.parse(JSON.stringify(selection)));
                exportSelection.pronunciationDictionary =
                    this.dictionaryService.addDictionary(exportSelection);
                exportSelection.glossaryEntries = this.glossaryService.addGlossaryUsingV1Id(exportSelection);
                UtilitiesService.downloadFile(this.formatSelection(exportSelection),
                                              selection.metaData.referenceStory + ".json");
            } else {
                this.saveUpdatedContent(selection);
            }
        });

    }

    private saveUpdatedContent(selection:ISelection): void {
        this.currentSourceSelection = selection;
        let glossaryEntries = this.glossaryService.addGlossaryUsingV1Id(this.currentSourceSelection);
        // word category is a measure of word "importance" and is used in speech recognition and word color feedback
        this.wordCategoryPipelineService.assignWordCategory(this.currentSourceSelection);
        this.saveSourceContent().subscribe(result => {
            this.logService.log("saved uninflated");
            this.currentSourceSelection = selection;
            this.currentSelection = (JSON.parse(JSON.stringify(this.currentSourceSelection)));
            this.currentSelection.pronunciationDictionary =
                this.dictionaryService.addDictionary(this.currentSelection);
            this.currentSelection.glossaryEntries = glossaryEntries;
            this.saveContent().subscribe(finalResult => {
                this.logService.log("Saved Content " + finalResult);
            });
        });
    }

    private fetchSharedData(): Observable<boolean> {

        return forkJoin([
            this.glossaryService.loadGlossary(),
            this.wordAudioService.loadWordAudioTable()]
        ).pipe(map(data => {
            return true;
        }));
    }

    private postSharedData(isTestRun: boolean): Observable<boolean> {
        if (isTestRun) {
            return of(true);
        } else {
            return forkJoin([
                this.glossaryService.postGlossary(),
                this.wordAudioService.postWordAudioTable()]
            ).pipe(map(data => {
                return true;
            }));
        }
    }

    public getQuestionReport(): Observable<Response> {
        this.logService.log("loading question report");
        return this.http.get(
            PipelineService.CONTENT_SERVER + "questionReport",
            { withCredentials: true }) as Observable<Response>;
    }

    private handleError<T>(operation = "operation", result?: T) {
        return (error: any): Observable<T> => {
            console.error(error); // log to console instead

            // TODO: better job of transforming error for user consumption
            const message = `${operation} failed: ${error.message}`;
            console.log(message);

            this.eventBus.next(new PipelineErrorEvent(message));

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

}
